import Head from 'next/head';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from './_app';
import React from 'react';
import Layout from '../index-layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';

const RestrictedTool = dynamic(() => import('@/components/RestrictedTool'), {
  ssr: true,
});
import { getTdkApi } from '@/lib/service/tdk';
import { faqJsonLd, page2JsonLd } from '@/lib/common/jsonld';
import { getPageContentContentApi } from '@/lib/service/home';
import { DynamicISRDebugPanel as ISRDebugPanel } from '@/components/ISRDebugPanel/dynamic';
import { ISR_REVALIDATE_TIME } from '@/lib/config/isr';
import { getFaqList } from '@/utils/landingData';
import { hyphenatedToTitleCase } from '@/shared/path-helper';

// ⭐ ISR: 转换为静态生成 + 7 天重新验证
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale = 'en' } = context;
  const resolvedUrl = '/onlyfans-restricted-words-checker';

  let currentURL = `${process.env.NEXT_APP_WEB_HOST}${resolvedUrl}`;
  if (locale != 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}${resolvedUrl}`;
  }
  const [localeRes, tdkData, res] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(resolvedUrl, 'en'),
    getPageContentContentApi(
      `/onlyfans-restricted-words-checker`,
      'en',
      '', // ISR 不支持 preview 参数
      '',
    ),
  ]);
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;

  // const articleList = await getArticleRecommendList(null, locale);
  const pageTitle = tdkData?.data?.title ?? tempData?.head_Restricted_Title ?? null;
  const pageDescription = tdkData?.data?.description ?? tempData?.head_Restricted_Desc ?? null;
  const jsonLd = page2JsonLd(
    {
      title: pageTitle,
      desc: pageDescription,
      url: currentURL,
    },
    { url: siteUrl },
  );

  const faqList = getFaqList(res);
  const faqJSONLD = faqJsonLd(
    {
      title: hyphenatedToTitleCase(resolvedUrl),
      desc: pageDescription,
      url: currentURL,
    },
    {
      url: siteUrl,
    },
    faqList,
  );

  return {
    props: {
      ...localeRes,
      herf: currentURL,
      locale,
      title: pageTitle,
      description: pageDescription,
      jsonLd: jsonLd,
      faqJSONLd: faqJSONLD,
      data: res.data,
      // keywords: tempData?.head_Meta_Keys ?? null,
      // ⭐ ISR 信息
      isrGeneratedAt: new Date().toISOString(),
      isrRevalidateIn: ISR_REVALIDATE_TIME,
    },
    revalidate: ISR_REVALIDATE_TIME,
  };
};

const Home: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props: any) => {
  return (
    <>
      <ISRDebugPanel
        generatedAt={props.isrGeneratedAt}
        revalidateIn={props.isrRevalidateIn}
        pageName="OnlyFans Restricted Words Checker"
        additionalInfo={{
          语言: props.locale,
        }}
      />
      <RestrictedTool data={props} />
    </>
  );
};
Home.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const jsonLd = page?.props?.jsonLd;
  const faqJSONLd = page?.props?.faqJSONLd;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta
            property="og:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta
            property="og:image:secure_url"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta
            name="twitter:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />

          <link
            rel="alternate"
            hrefLang="en"
            href={`${process.env.NEXT_APP_WEB_HOST}/en/onlyfans-restricted-words-checker`}
          />
          <link
            rel="alternate"
            hrefLang="zh"
            href={`${process.env.NEXT_APP_WEB_HOST}/zh/onlyfans-restricted-words-checker`}
          />
          <link
            rel="alternate"
            hrefLang="tw"
            href={`${process.env.NEXT_APP_WEB_HOST}/tw/onlyfans-restricted-words-checker`}
          />
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${process.env.NEXT_APP_WEB_HOST}/onlyfans-restricted-words-checker`}
          />
          <link
            rel="alternate"
            hrefLang="ja"
            href={`${process.env.NEXT_APP_WEB_HOST}/ja/onlyfans-restricted-words-checker`}
          />
          <link
            rel="alternate"
            hrefLang="ko"
            href={`${process.env.NEXT_APP_WEB_HOST}/ko/onlyfans-restricted-words-checker`}
          />
          <link
            rel="alternate"
            hrefLang="pt"
            href={`${process.env.NEXT_APP_WEB_HOST}/pt/onlyfans-restricted-words-checker`}
          />
          <link
            rel="alternate"
            hrefLang="de"
            href={`${process.env.NEXT_APP_WEB_HOST}/de/onlyfans-restricted-words-checker`}
          />
          <link
            rel="alternate"
            hrefLang="es"
            href={`${process.env.NEXT_APP_WEB_HOST}/es/onlyfans-restricted-words-checker`}
          />
          <link
            rel="alternate"
            hrefLang="it"
            href={`${process.env.NEXT_APP_WEB_HOST}/it/onlyfans-restricted-words-checker`}
          />

          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
          {faqJSONLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJSONLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default Home;
